import { PaperclipIcon, XIcon } from "lucide-react";
import { useRef } from "react";
import { AttachmentScanStatus } from "@/api/tickets";
import { Button } from "@/components/ui/button";
import { Field, FieldDescription, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { cn } from "@/utils";
import { useIntakeMessages } from "./intake-messages";
import type { useTicketIntake } from "./use-ticket-intake";

export function TicketIntakeAttachments({ form }: { form: ReturnType<typeof useTicketIntake> }) {
  const m = useIntakeMessages();
  const input = useRef<HTMLInputElement>(null);
  const { session, busy } = form;

  return (
    <Field>
      <div className="flex items-center justify-between gap-3">
        <FieldLabel htmlFor="intake-attachments">{m.attachments}</FieldLabel>
        <Button disabled={busy || !session} type="button" variant="outline" size="sm" onClick={() => input.current?.click()}>
          <PaperclipIcon data-icon="inline-start" />
          {m.upload}
        </Button>
      </div>
      <Input
        ref={input}
        className="sr-only"
        id="intake-attachments"
        type="file"
        multiple
        disabled={busy || !session}
        tabIndex={-1}
        aria-label={m.upload}
        aria-describedby="intake-attachments-hint"
        onChange={(event) => {
          void form.upload(event.currentTarget.files);
          event.currentTarget.value = "";
        }}
      />
      <FieldDescription id="intake-attachments-hint">{m.scanHint}</FieldDescription>
      {session?.attachments.length ? (
        <ul className="flex flex-col gap-2" aria-label={m.attachments}>
          {session.attachments.map((file) => (
            <li className="flex items-center gap-3 rounded-lg bg-muted/40 px-3 py-2 text-sm" key={file.id}>
              <PaperclipIcon className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
              <span className="min-w-0 flex-1 break-all">{file.fileName}</span>
              <span className={cn("shrink-0 text-muted-foreground", file.scanStatus === AttachmentScanStatus.Rejected && "text-destructive")}>
                {file.scanStatus === AttachmentScanStatus.Clean
                  ? m.scanClean
                  : file.scanStatus === AttachmentScanStatus.Rejected
                    ? m.scanRejected
                    : m.scanPending}
              </span>
              <Button
                aria-label={`${m.remove} ${file.fileName}`}
                disabled={busy || !session}
                type="button"
                size="icon-sm"
                variant="ghost"
                onClick={() => void form.removeAttachment(file.id)}
              >
                <XIcon />
              </Button>
            </li>
          ))}
        </ul>
      ) : null}
      {session?.attachments.some((file) => file.scanStatus !== AttachmentScanStatus.Clean) ? (
        <Button className="self-start" disabled={busy || !session} type="button" size="sm" variant="ghost" onClick={() => void form.refreshAttachments()}>
          {m.refreshScan}
        </Button>
      ) : null}
    </Field>
  );
}
